import { GraphQlRequestService } from '../../api/graphql-request-service';
import { Role } from '../../permissions/permissions';
import { Domains } from '../constants';
import { Hasher } from './hasher';
import {
  ApiValueDataType,
  HasDependencies,
  hashString,
  SQLQuery,
} from './types';
import { handleGQLErrors } from './utils';

export interface AuthenticatedBackendService extends HasDependencies {
  fetchDataCustomSqlQuery: <T>(
    dataMapper: (data: ApiValueDataType[][]) => T[]
  ) => (querySql: SQLQuery) => Promise<T[]>;
  fetchDataWhitelistedQuery: <T>(
    dataMapper: (data: ApiValueDataType[][]) => T[]
  ) => (querySql: SQLQuery) => Promise<T[]>;
}

// WARNING: Don't use this service directly. Instead, create a new service with a more descriptive name
//          such as EmployeeService and use this service as the underlying service to fetch the data
//          with the appropriate dataMapper to transform the raw data into the expected type (e.g. Employee)
export class AuthenticatedGraphQLBackendService
  implements AuthenticatedBackendService
{
  constructor(
    readonly graphQlRequestService: GraphQlRequestService,
    readonly domain: Domains,
    readonly executorRole: Role,
    readonly simulateRole: Role | null
  ) {}

  public getDeps = () => [
    hashString([
      ...this.graphQlRequestService.getDeps(),
      this.domain,
      Hasher.hash(this.executorRole),
      Hasher.hash(this.simulateRole),
    ]),
  ];

  public fetchDataCustomSqlQuery =
    <T>(dataMapper: (data: ApiValueDataType[][]) => T) =>
    async (querySql: SQLQuery) => {
      const result = await handleGQLErrors(
        this.graphQlRequestService.graphQlSdk.executeCustomSqlQuery({
          domain: this.domain,
          querySql,
          selectedExecutorRole: this.executorRole.id,
          simulateRole: this.simulateRole?.id ?? null,
          disableNestLoop: null,
        })
      );
      return dataMapper(result.executeCustomSqlQuery?.data ?? []);
    };

  public fetchDataWhitelistedQuery =
    <T>(dataMapper: (data: ApiValueDataType[][]) => T) =>
    async (querySql: SQLQuery) => {
      const result = await handleGQLErrors(
        this.graphQlRequestService.graphQlSdk.executeWhitelistedSqlQuery({
          domain: this.domain,
          querySql,
          selectedExecutorRole: this.executorRole.id,
          simulateRole: this.simulateRole?.id ?? null,
        })
      );
      return dataMapper(result.executeWhitelistedSqlQuery?.data ?? []);
    };
}
